import { signal, untracked } from '@angular/core';

const SPRITE_URL = '/assets/icons/complexcare-icons.svg';
const SPRITE_ID = 'hx-shared-icon-sprite';
const SPRITE_HREF = /(href=["'])(?:[^"'#]*complexcare-icons\.svg)?#(ccs-[\w-]+)(["'])/g;

function createSpriteState() {
  return {
    status: signal<'idle' | 'loading' | 'ready' | 'failed'>('idle'),
  };
}

type SpriteDocument = Document & { __hxSharedIconSprite?: ReturnType<typeof createSpriteState> };

export function getIconSprite(document: Document) {
  const owner = document as SpriteDocument;
  const state = (owner.__hxSharedIconSprite ??= createSpriteState());

  return {
    status: state.status.asReadonly(),
    load(retry = false): void {
      const status = state.status();
      if ((status !== 'idle' && !(retry && status === 'failed')) || !document.defaultView) return;
      if (document.getElementById(SPRITE_ID)) {
        state.status.set('ready');
        return;
      }

      state.status.set('loading');
      void fetch(SPRITE_URL, { cache: 'force-cache' })
        .then((response) => {
          if (!response.ok) throw new Error('Unable to load icon sprite');
          return response.text();
        })
        .then((source) => {
          const parsed = new DOMParser().parseFromString(source, 'image/svg+xml');
          if (parsed.querySelector('parsererror') || parsed.documentElement.localName !== 'svg') {
            throw new Error('Invalid icon sprite');
          }
          const sprite = document.importNode(parsed.documentElement, true);
          sprite.id = SPRITE_ID;
          sprite.setAttribute('aria-hidden', 'true');
          sprite.setAttribute('focusable', 'false');
          sprite.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none');
          document.body.prepend(sprite);
          state.status.set('ready');
        })
        .catch(() => state.status.set('failed'));
    },
  };
}

export function spriteSymbolHref(symbolId: string): string {
  if (typeof document !== 'undefined' && typeof fetch !== 'undefined') {
    untracked(() => getIconSprite(document).load(true));
  }
  return `#${symbolId}`;
}

/** Rewrites sprite references in stored rich-text HTML to same-document hrefs. */
export function localizeSpriteHrefs(html: string): string {
  if (!html || !html.includes('#ccs-')) return html;
  return html.replace(SPRITE_HREF, (_match, prefix: string, id: string, quote: string) => {
    return `${prefix}${spriteSymbolHref(id)}${quote}`;
  });
}
